import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Play } from 'lucide-react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Restrict allowed HTML tags/attributes to a safe subset for article content
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'a', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'span', 'img', 'hr'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'title', 'class'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/articles/${slug}`)
      .then(r => { if (!cancelled) setArticle(r.data); })
      .catch((err) => {
        if (!cancelled) console.error('Failed to load article:', err);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const sanitizedHtml = useMemo(() => {
    if (!article?.content) return '';
    const isHtml = /<[a-z][\s\S]*>/i.test(article.content);
    return isHtml ? DOMPurify.sanitize(article.content, SANITIZE_CONFIG) : '';
  }, [article?.content]);

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-8 h-8 border-4 border-[#D4AF37] border-t-transparent rounded-full" /></div>;
  if (!article) return <div className="text-center py-20"><h2 className="text-2xl font-bold" style={{ fontFamily: "'Poppins', sans-serif" }}>Article Not Found</h2><Link to="/education" className="text-[#D4AF37] mt-4 inline-block">Back to Education Hub</Link></div>;

  const renderMarkdown = (content) => {
    return content.split('\n').map((line, i) => {
      // Build a stable composite key from line content + position to avoid pure-index keys
      const key = `${i}-${line.slice(0, 24)}`;
      if (line.startsWith('## ')) return <h2 key={key} className="text-2xl font-bold mt-8 mb-4" style={{ fontFamily: "'Poppins', sans-serif" }}>{line.replace('## ', '')}</h2>;
      if (line.startsWith('**') && line.endsWith('**')) return <p key={key} className="font-bold mb-2">{line.replace(/\*\*/g, '')}</p>;
      if (line.startsWith('- ')) return <li key={key} className="ml-4 mb-1">{line.replace('- ', '')}</li>;
      if (line.match(/^\d+\.\s/)) return <li key={key} className="ml-4 mb-1 list-decimal">{line.replace(/^\d+\.\s/, '')}</li>;
      if (line.startsWith('*') && line.endsWith('*')) return <p key={key} className="text-sm italic text-gray-500 mt-4">{line.replace(/\*/g, '')}</p>;
      if (!line.trim()) return <br key={key} />;
      return <p key={key} className="mb-3">{line}</p>;
    });
  };

  const renderContent = () => {
    if (sanitizedHtml) {
      // Content is sanitized via DOMPurify with a strict allowlist (see SANITIZE_CONFIG above)
      return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
    }
    return renderMarkdown(article.content);
  };

  return (
    <div data-testid="article-page">
      <SEO
        title={article.title}
        description={article.excerpt}
        path={`/education/${article.slug}`}
        image={article.image_url}
        type="article"
      />
      <section className="bg-[#0A0A0A] py-16">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/education" className="inline-flex items-center gap-2 text-gray-400 hover:text-[#D4AF37] mb-6 text-sm" data-testid="back-to-education"><ArrowLeft size={16} /> Education Hub</Link>
          <span className="text-xs uppercase tracking-wider text-[#D4AF37] font-semibold block mb-3" style={{ fontFamily: "'Poppins', sans-serif" }}>{article.category}</span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white" style={{ fontFamily: "Cinzel, serif" }}>{article.title}</h1>
          <div className="flex items-center gap-2 text-gray-400 text-sm mt-4"><Calendar size={14} />{new Date(article.created_at).toLocaleDateString('en-NZ', { year: 'numeric', month: 'long', day: 'numeric' })}</div>
        </div>
      </section>
      {article.external_video_url && article.image_url ? (
        <div className="max-w-4xl mx-auto px-6 -mt-4">
          <a href={article.external_video_url} target="_blank" rel="noopener noreferrer" className="block relative group rounded-sm overflow-hidden" data-testid="article-external-video">
            <img src={article.image_url} alt={article.title} className="w-full h-64 md:h-96 object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-black/40 group-hover:bg-black/55 transition-colors flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-[#D4AF37] flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                <Play size={36} className="text-[#0A0A0A] ml-1" fill="#0A0A0A" />
              </div>
            </div>
            <span className="absolute bottom-4 left-4 text-white text-sm font-medium bg-black/60 px-3 py-1 rounded" style={{ fontFamily: "'Poppins', sans-serif" }}>
              {article.external_video_url.includes('youtube') ? 'Watch on YouTube' : 'Watch Video'}
            </span>
          </a>
        </div>
      ) : article.video_url ? (
        <div className="max-w-4xl mx-auto px-6 -mt-4">
          <video src={article.video_url} poster={article.video_url.replace('.mp4', '-poster.jpg')} className="w-full h-64 md:h-96 object-cover rounded-sm" controls muted autoPlay loop playsInline data-testid="article-video" />
        </div>
      ) : article.image_url ? (
        <div className="max-w-4xl mx-auto px-6 -mt-4">
          <img src={article.image_url} alt={article.title} className="w-full h-64 md:h-96 object-cover rounded-sm" loading="lazy" />
        </div>
      ) : null}
      <article className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-lg max-w-none text-gray-700" style={{ fontFamily: "'Poppins', sans-serif" }}>
          {renderContent()}
        </div>
        <div className="mt-12 pt-8 border-t border-gray-200">
          <p className="text-sm text-gray-500 italic" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Disclaimer: This information is general in nature and does not constitute personalised financial advice. Past performance is not indicative of future results. We recommend seeking independent financial advice before making investment decisions.
          </p>
        </div>
      </article>
    </div>
  );
}
